import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { FR_PDF_TOOLS, FR_PDF_CATEGORIES, type FrPdfCategory } from "@/lib/fr-pdf-catalog";
import { FileText, ShieldCheck, Zap, Lock, ArrowRight, CheckCircle2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/fr/pdf-tools/")({
  head: () => ({
    meta: [
      { title: "Outils PDF Gratuits en Ligne — PDF Toolkit Pro | Picamini.online" },
      {
        name: "description",
        content:
          "26+ outils PDF gratuits en ligne : fusionner PDF, diviser, compresser, modifier, convertir en Word/Excel et protéger par mot de passe. 100% privé dans votre navigateur sans transfert de fichier.",
      },
      {
        name: "keywords",
        content:
          "outils pdf gratuits, fusionner pdf, diviser pdf, compresser pdf, modifier pdf en ligne, convertir jpg en pdf, pdf en word gratuit, proteger pdf",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Outils PDF Gratuits en Ligne — 100% Privés dans votre Navigateur" },
      {
        property: "og:description",
        content: "Traitez vos fichiers PDF de manière ultra rapide et confidentielle sans rien envoyer sur des serveurs externes.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/fr/pdf-tools" },
      { property: "og:locale", content: "fr_FR" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/fr/pdf-tools" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/pdf-tools" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/pdf-tools" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/pdf-tools" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/pdf-tools" },
      { rel: "alternate", hrefLang: "fr", href: "https://picamini.online/fr/pdf-tools" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/pdf-tools" },
    ],
  }),
  component: FrPdfHubPage,
});

function FrPdfHubPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<FrPdfCategory | "Tous">("Tous");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FR_PDF_TOOLS.filter((t) => {
      const matchesCategory = category === "Tous" || t.category === category;
      const matchesQuery =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.tagline.toLowerCase().includes(q) ||
        t.keywords.some((k) => k.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader lang="fr" />

      <main className="flex-1">
        {/* HERO */}
        <section className="bg-gradient-to-b from-primary/5 via-background to-background py-16 sm:py-20 border-b border-border/50">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 text-center space-y-4">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary">
              <ShieldCheck className="size-4" /> PDF Toolkit Pro — 100% Traitement Local dans le Navigateur
            </span>

            <h1 className="text-3xl font-extrabold tracking-tight sm:text-5xl text-foreground max-w-4xl mx-auto">
              Outils PDF Gratuits en Ligne et Confidentiels
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Fusionnez, divisez, compressez, modifiez et convertissez vos fichiers PDF directamente dans votre navigateur. Vos documents ne quittent jamais votre ordinateur.
            </p>

            {/* SEARCH BAR */}
            <div className="mx-auto mt-6 flex max-w-lg items-center gap-2 rounded-2xl bg-card p-2 shadow-md border border-border">
              <Search className="size-5 ml-2 text-muted-foreground shrink-0" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un outil PDF (ex: fusionner, compresser, protéger)…"
                aria-label="Rechercher des outils PDF"
                className="border-0 bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:ring-0 text-sm sm:text-base font-medium"
              />
            </div>
          </div>
        </section>

        {/* VALUE PROPOSITIONS */}
        <section className="py-8 bg-card border-b border-border/60">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 sm:grid-cols-3 text-center">
              <div className="flex items-center justify-center gap-3 p-2">
                <Lock className="size-5 text-primary" />
                <span className="text-sm font-bold text-foreground">100% Confidentiel & RGPD Native</span>
              </div>
              <div className="flex items-center justify-center gap-3 p-2">
                <Zap className="size-5 text-primary" />
                <span className="text-sm font-bold text-foreground">Traitement Ultra Rapide (Sans Téléversement)</span>
              </div>
              <div className="flex items-center justify-center gap-3 p-2">
                <CheckCircle2 className="size-5 text-primary" />
                <span className="text-sm font-bold text-foreground">Gratuit Sans Inscription Ni Limite</span>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORIES FILTER & TOOLS GRID */}
        <section className="py-12 mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            <Button
              size="sm"
              variant={category === "Tous" ? "default" : "outline"}
              onClick={() => setCategory("Tous")}
              className="rounded-xl font-bold"
            >
              Tous les outils PDF ({FR_PDF_TOOLS.length})
            </Button>
            {FR_PDF_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                size="sm"
                variant={category === cat ? "default" : "outline"}
                onClick={() => setCategory(cat)}
                className="rounded-xl font-semibold"
              >
                {cat}
              </Button>
            ))}
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((tool) => (
              <Link
                key={tool.slug}
                to="/fr/pdf-tools/$slug"
                params={{ slug: tool.slug }}
                className="group rounded-xl border border-border/80 bg-card p-5 hover:border-primary/50 hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <h3 className="font-bold text-foreground text-lg group-hover:text-primary transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {tool.description}
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-1 text-xs font-bold text-primary">
                  Ouvrir l'outil <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center space-y-3">
              <p className="text-lg font-semibold text-muted-foreground">
                Aucun outil PDF ne correspond à "{query}".
              </p>
              <Button variant="outline" onClick={() => { setQuery(""); setCategory("Tous"); }}>
                Réinitialiser le filtre
              </Button>
            </div>
          )}
        </section>
      </main>

      <SiteFooter lang="fr" />
    </div>
  );
}

